import { type ReactElement, useRef, useState } from 'react'
import { ProfilesList } from '~/components/Global/Elements/ProfilesList/ProfilesList'
import { profilesListStandardHeight } from '~/components/Global/Elements/ProfilesList/ProfilesList.css'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import type { Action } from '~/domains/actions/types'
import { formatName } from '~/utils/names'
import { TaskParticipants } from '../TaskAside/TaskParticipants'
import {
  taskCellPersons,
  taskPersonsEmpty,
  taskPersonsLabel,
  taskPersonsMore,
  taskPersonsPopover
} from './TaskCard.css'

const hoverDelay = { open: 250, close: 200 }

type TaskPersonsProps = {
  task: Action
  /** Route quick actions through the legacy global bridges (defaults to legacy page detection). */
  legacyBridges?: boolean
}

/**
 * Persons a task is linked to (applicants, contacts with their company logo):
 * avatars plus names, opening the same participant cards as the task aside.
 */
export const TaskPersons = ({
  task,
  legacyBridges = window.JobConfig !== undefined
}: TaskPersonsProps): ReactElement => {
  const [isOpen, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const applicants = Object.values(task.applicants ?? {})
  const contacts = Object.values(task.contacts ?? {})
  const names = [
    ...applicants.map((applicant) => formatName(applicant)),
    ...contacts.map((contact) =>
      contact.company?.name
        ? `${formatName(contact)} · ${contact.company.name}`
        : formatName(contact)
    )
  ]

  // Users invited on the task besides the assignee, who has his own column
  const otherUsers = (task.users ?? []).filter(
    (user) => user.id !== task.user?.id
  )
  const moreLabel =
    otherUsers.length > 0
      ? `+${otherUsers.length} : ${otherUsers.map((user) => formatName(user)).join(', ')}`
      : null

  if (names.length === 0 && otherUsers.length === 0) {
    return (
      <div className={taskCellPersons}>
        <span className={taskPersonsEmpty}>—</span>
      </div>
    )
  }

  // The popover opens on hover only: a click on the cell opens the task aside like the rest of the row,
  // while clicks inside the popover (participant cards) stay there.
  return (
    <>
      <div
        ref={triggerRef}
        className={taskCellPersons}
        data-onboarding="task-persons"
      >
        {names.length > 0 && (
          <ProfilesList
            applicantIds={applicants.map((applicant) => applicant.id)}
            contactIds={contacts.map((contact) => contact.id)}
            maxProfilesCount={3}
            align="left"
            className={profilesListStandardHeight}
            noTooltip
          />
        )}
        {names.length > 0 && (
          <span className={taskPersonsLabel}>{names.join(', ')}</span>
        )}
        {otherUsers.length > 0 && (
          <span className={taskPersonsMore} title={moreLabel ?? undefined}>
            +{otherUsers.length}
          </span>
        )}
      </div>
      <Dropdown
        isOpen={isOpen}
        onopen={() => setOpen(true)}
        onclose={() => setOpen(false)}
        hoverDelay={hoverDelay}
        dropdownRef={triggerRef}
        className={taskPersonsPopover}
        placement="bottom-start"
      >
        <TaskParticipants
          task={task}
          onAction={() => setOpen(false)}
          legacyBridges={legacyBridges}
        />
      </Dropdown>
    </>
  )
}
